"use client";
import React, { useState, useRef, useCallback } from "react";
import ToastContext from "@/contexts/ToastContext";
import AppToast from "@/components/ui/AppToast";
import { normalizeToastVariant } from "@/components/ui/toastPalette";

type ToastVariant = "success" | "danger" | "info" | "warning";

interface ToastMessage {
    id: number;
    message: string;
    variant?: ToastVariant;
    delay?: number;
    title?: string;
    createdAt: Date;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
    const [toasts, setToasts] = useState<ToastMessage[]>([]);
    const [closing, setClosing] = useState<{ [id: number]: boolean }>({});
    const timers = useRef<{ [id: number]: NodeJS.Timeout }>({});

    const AUTO_DISMISS =
        Number(process.env.NEXT_PUBLIC_TOAST_AUTO_DISMISS) || 5000; // ms

    const handleClose = useCallback((id: number) => {
        setClosing((prev) => ({ ...prev, [id]: true }));
        setTimeout(() => {
            setToasts((prev) => prev.filter((t) => t.id !== id));
            setClosing((prev) => {
                const copy = { ...prev };
                delete copy[id];
                return copy;
            });
            if (timers.current[id]) {
                clearTimeout(timers.current[id]);
                delete timers.current[id];
            }
        }, 400); // match CSS transition duration
    }, []);

    const show = useCallback(
        (toast: Omit<ToastMessage, "id" | "createdAt">) => {
            const id = Date.now() + Math.random();
            setToasts((prev) => [
                ...prev,
                { ...toast, id, createdAt: new Date() },
            ]);
            timers.current[id] = setTimeout(
                () => handleClose(id),
                toast.delay ?? AUTO_DISMISS
            );
        },
        [AUTO_DISMISS, handleClose]
    );

    return (
        <ToastContext.Provider value={{ show }}>
            {children}
            <div
                style={{
                    position: "fixed",
                    bottom: 24,
                    right: 24,
                    zIndex: 9999,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 8,
                }}
            >
                {toasts.map((toast) => {
                    const normalizedVariant = normalizeToastVariant(
                        toast.variant
                    );
                    return (
                        <div
                            key={toast.id}
                            style={{
                                transition:
                                    "transform 0.4s cubic-bezier(.4,0,.2,1), opacity 0.4s cubic-bezier(.4,0,.2,1)",
                                transform: closing[toast.id]
                                    ? "translateX(120%)"
                                    : "translateX(0)",
                                opacity: closing[toast.id] ? 0 : 1,
                            }}
                        >
                            <AppToast
                                title={
                                    toast.title ||
                                    (normalizedVariant === "danger"
                                        ? "Error"
                                        : normalizedVariant === "warning"
                                          ? "Warning"
                                          : normalizedVariant === "info"
                                            ? "Info"
                                            : "Success")
                                }
                                message={toast.message}
                                variant={normalizedVariant}
                                onClose={() => handleClose(toast.id)}
                                createdAt={toast.createdAt}
                            />
                        </div>
                    );
                })}
            </div>
        </ToastContext.Provider>
    );
}
